import LinkOutlined from "@mui/icons-material/LinkOutlined";
import type { FC } from "react";
import { Stack, TextField, InputAdornment, Typography, Button } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { serviceURL } from "../../../../utils/service-url";
import { QRCode } from "../qr-code/qr-code";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { authHeader } from "../../../../utils/auth-header";
import { SupabaseMomentService } from "../../server/supabase-moment-service";
import { useState } from "react";
import { useToken } from "../../../../server/auth/use-token";
import { showroomClient } from "../../../showroom/client";

export const SharingLinkField: FC = () => {
  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: ["showroom_key"] });
  const [showroomKey, momentId, title] = getValues(["showroom_key", "id", "title"]);

  const [revoking, setRevoking] = useState(false);
  const getToken = useToken();

  const handleUnpublish = useSupabaseCallback(
    async ({ supabase }) => {
      if (!showroomKey) return;

      setRevoking(true);

      const showRoomResult = await showroomClient.POST("/unpublish", {
        body: { short_id: showroomKey },
        ...authHeader(await getToken()),
      });

      if (showRoomResult.error !== undefined) {
        setRevoking(false);
        toast.error("Could not revoke sharing link");
        return;
      }

      const momentUpdate = await new SupabaseMomentService(supabase).update(momentId, {
        showroom_key: null,
      });

      if (momentUpdate.error !== null) {
        setRevoking(false);
        toast.error("Could not revoke sharing link");
        return;
      }

      setRevoking(false);
      setValue("showroom_key", null);
    },
    [getToken, momentId, setValue, showroomKey],
  );

  if (!showroomKey) {
    return null;
  }

  const sharingUrl = `${serviceURL("showroom")}${showroomKey}`;

  return (
    <Stack direction="column" spacing={2}>
      <TextField
        label="Link"
        value={sharingUrl}
        disabled
        variant="outlined"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  component="span"
                  onClick={() => {
                    void navigator.clipboard.writeText(sharingUrl);
                    toast.success("Link copied to clipboard");
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    color: "primary.main",
                  }}
                >
                  <LinkOutlined fontSize="small" />
                  Copy Link
                </Typography>
              </InputAdornment>
            ),
          },
        }}
      />
      <QRCode title={title} link={sharingUrl} />
      <Typography variant="body2" fontSize="14px" fontWeight={700}>
        Stop sharing
      </Typography>
      <Typography variant="body2" fontSize="14px">
        You can stop sharing this Communication at any time. If you want to share again later, come
        back and generate the sharing link again.
      </Typography>
      <Button
        variant="text"
        color="error"
        data-analytics-id="moment-form-generate-sharing-link-revoke-button"
        onClick={() => void handleUnpublish()}
        loading={revoking}
        sx={{ alignSelf: "flex-start" }}
      >
        Stop sharing
      </Button>
    </Stack>
  );
};
